<template>
  <v-menu
    transition="slide-y-transition"
    bottom
    :close-on-content-click="false"
  >
    <template v-slot:activator="{ on }">
      <v-btn flat color="white" v-on="on" :loading="loading" :disabled="loading">
        <span class="font-weight-medium" v-text="selected"></span>
        <v-icon>expand_more</v-icon>
      </v-btn>
    </template>
    <v-list>
      <v-list-tile
        v-for="(lang, i) in available"
        :key="i"
        :disabled="lang.name === current"
        @click="onClickLang(lang)">
        <v-list-tile-avatar>
          <span class="font-weight-medium" v-text="lang.name.toUpperCase()"></span>
        </v-list-tile-avatar>
        <v-list-tile-content>
          <v-tooltip bottom>
            <v-btn v-if="lang.state === 'draft'" slot="activator" small icon color="warning">
              <v-icon small>edit</v-icon>
            </v-btn>
            <v-btn v-if="lang.state === 'published'" slot="activator" small icon color="accent">
              <v-icon small>done</v-icon>
            </v-btn>
            <v-btn v-if="lang.state === 'nonexistent'" slot="activator" small icon color="primary">
              <v-icon small>add</v-icon>
            </v-btn>
            <span v-text="$t(`message.content.language.state.${lang.state}`)"></span>
          </v-tooltip>
        </v-list-tile-content>
      </v-list-tile>
    </v-list>
  </v-menu>
</template>

<script>
import api from '@/modules/content/editor/api';
import { mapGetters } from 'vuex';
import Storable from '@/mixins/storable';
import toast from '@/modules/toast/services';

const NON_EXISTENT_LANGUAGE = 'nonexistent';

export default {
  name: 'nx-default-language-selector',
  mixins: [Storable],
  props: {
    current: {
      type: String,
      required: true,
    },
    available: {
      type: Array,
      required: true,
    },
    editorStorePath: {
      type: [String, Array],
      required: true,
    },

  },
  data() {
    return {
      loading: false,
    };
  },
  methods: {
    onClickLang(lang) {
      // No lo volvemos a setear como false al final de este método porque
      // cuando no se hace la petición de crear el lenguaje, no se llega a apreciar
      // el spinner, sin embargo en ese punto está haciendo la petición para cargar el
      // editor del contenido. Dando la impresión de que ha terminado de cargar, pero en realidad
      // sigue haciendo cosas. Por eso lo dejamos como true hasta que cargue la ruta del editor del contenido.
      // Además, cuando vuelve a cargar este componente con el editor del contenido, loading vuelve a tener
      // su valor por defecto, false.
      this.loading = true;
      this.selectLang(lang);
    },
    async selectLang(lang) {
      if (this.contentLanguageIsNonExistent(lang.state)) {
        api.addContentLanguage(this.contentId, this.state.relativeVersionId, lang.name)
          .then((res) => {
            this.navigateToContentLanguage(res);
          })
          .catch((error) => {
            const { response } = error;
            let errorMessage = this.$t('message.error.translation');
            if (response.data.cmsMessage !== undefined) {
              errorMessage = response.data.cmsMessage;
            }

            toast.error(errorMessage);
            this.loading = false;
          });
      } else {
        this.navigateToContentLanguage(lang);
      }
    },
    contentLanguageIsNonExistent(langState) {
      return NON_EXISTENT_LANGUAGE === langState;
    },
    navigateToContentLanguage(lang) {
      if (this.languageHasSpecificSite(lang)) {
        return this.navigateToContentLanguageSite(lang);
      }

      // cuando el lenguaje no existe y lo creamos, el "nombre" del lenguaje viene en
      // "language", sin embargo cuando sí existe y lo seleccionamos, viene en "name"
      const language = lang.name || lang.language;

      // lo mismo para el contenido del id, entre "contentId" cuando ya existe e "id" cuando lo acabamos de crear
      const destContentId = lang.contentId || lang.id;

      const currentLanguage = this.available.find(l => l.name === this.state.currentLanguage);

      // Si nuestro contenido ha generado un contenido para otro idioma, en el mismo site, pero el ID de contenido
      // es distinto, nos vamos a ese editor de contenido
      if (currentLanguage
          && currentLanguage.contentId !== destContentId
      ) {
        return this.$router.push({
          name: 'content.editor',
          params: {
            id: destContentId,
            type: this.contentType,
            site: lang.site,
          },
        });
      }

      return this.$router.push({
        query: { language },
        params: {
          relativeVersionId: null,
        },
      });
    },
    languageHasSpecificSite(lang) {
      const { site } = lang;
      return this.site.idAlias !== site;
    },
    async navigateToContentLanguageSite(lang) {
      const siteId = this.getSiteFromIdAlias(lang.site).id;
      // Actualiza el site para que la cabecera HTTP X-Site-id sea igual al site que intentamos cargar
      await this.$store.dispatch('SET_SITE', siteId);
      const id = lang.id || lang.contentId;
      await this.$router.push({ name: 'content.editor', params: { id, type: this.contentType, site: lang.site } });
    },
  },
  computed: {
    ...mapGetters(['getSiteFromIdAlias', 'site']),
    storePath() {
      return this.editorStorePath;
    },
    contentType() {
      return this.getters.type;
    },
    contentId() {
      return this.getters.id;
    },
    selected() {
      return this.current.toUpperCase();
    },
  },
};
</script>
